// Workflows tab (project detail, plan §2 WS6; visual editor ontwerp §3.1) —
// a read-only summary of every workflow a project has: states as cards in
// definition order with their kind, role and on_success/on_failure/
// on_approve/on_reject arrows, plus validation errors/warnings
// (`validateProjectWorkflows`, plan §0.1/§1). Each card links to "Open
// editor" (the visual canvas, `workflow-editor.tsx`); **New workflow**
// opens the editor blank or from a template — nothing is added to the
// project until the editor's own Save (ontwerp §3.1/§6), unlike the old
// "Add from template" which wrote immediately.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { DEFAULT_SUBTASKS_MAX_PARALLEL, type Project, type WorkflowDefinition, type WorkflowState } from '@goblic/shared'
import { workflowsApi } from '../../lib/api-client'
import {
  Dialog,
  EmptyState,
  ErrorNote,
  Field,
  LoadingRows,
  Notice,
  Panel,
} from '../ui'
import { useWorkspaceId } from '../../lib/auth'
import { useDialogs } from '../dialogs'
import { humanize } from '../../lib/format'
import { ClayIcon } from '../goblin-art'

const KIND_LABEL: Record<WorkflowState['kind'], string> = {
  agent: 'Agent',
  human: 'Human',
  terminal: 'Terminal',
  decision: 'Decision',
  subtasks: 'Subtasks',
}

const KIND_TONE: Record<WorkflowState['kind'], string> = {
  agent: 'bg-info-soft text-info',
  human: 'bg-signal-soft text-signal',
  terminal: 'bg-sunken text-ink-2',
  decision: 'bg-moss-soft text-moss-strong',
  subtasks: 'bg-sunken text-ink-2 ring-1 ring-inset ring-line-strong',
}

function KindBadge({ kind }: { kind: WorkflowState['kind'] }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${KIND_TONE[kind]}`}
    >
      {KIND_LABEL[kind]}
    </span>
  )
}

/** The state's `on*` targets, labeled by which run/human outcome takes them. */
function stateTransitions(
  state: WorkflowState
): { label: string; target: string; otherWorkflow?: boolean }[] {
  const out: { label: string; target: string; otherWorkflow?: boolean }[] = []
  const subtasks = state.kind === 'subtasks'
  if (state.onSuccess) out.push({ label: subtasks ? 'All done' : 'On success', target: state.onSuccess })
  if (state.onFailure) out.push({ label: subtasks ? 'Not all succeeded' : 'On failure', target: state.onFailure })
  if (state.onBlocked) out.push({ label: 'On blocked', target: state.onBlocked })
  if (state.onApprove) out.push({ label: 'On approve', target: state.onApprove })
  if (state.onReject) out.push({ label: 'On reject', target: state.onReject })
  for (const option of state.options ?? []) {
    if (option.nextWorkflow) {
      const { workflow, state: entry } = option.nextWorkflow
      out.push({ label: option.value, target: entry ? `${workflow} · ${entry}` : workflow, otherWorkflow: true })
    } else if (option.next) out.push({ label: option.value, target: option.next })
  }
  return out
}

function stateSummary(state: WorkflowState): string {
  const parts: string[] = []
  if (state.kind === 'agent') {
    parts.push(
      state.agentRole ? `Handled by ${humanize(state.agentRole)}` : 'No role set'
    )
  } else if (state.kind === 'human') {
    parts.push('Waits for a person to approve or reject')
  } else if (state.kind === 'decision') {
    parts.push(state.question?.trim() ? `Asks: ${state.question.trim()}` : 'Asks a question')
    if (state.variable) parts.push(`answer saved as ${state.variable}`)
  } else if (state.kind === 'subtasks') {
    parts.push('Waits for its subtasks')
    parts.push(state.childWorkflow ? `they run through workflow ${state.childWorkflow}` : 'they run through the project default workflow')
    parts.push(`up to ${state.maxParallel ?? DEFAULT_SUBTASKS_MAX_PARALLEL} at once`)
  } else if (state.outcome) {
    parts.push(`Ends the task: ${humanize(state.outcome)}`)
  }
  if (state.requires.length) {
    parts.push(
      `needs ${state.requires.map((r) => humanize(r).toLowerCase()).join(', ')}`
    )
  }
  if (state.freshContext) parts.push('starts with a fresh context')
  return parts.join(', ')
}

function StateCard({ state }: { state: WorkflowState }) {
  const transitions = stateTransitions(state)
  return (
    <li className="rounded-control border border-line-strong bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-ink">{humanize(state.name)}</span>
        <KindBadge kind={state.kind} />
        {state.maxRounds ? (
          <span className="text-sm text-ink-3">Max {state.maxRounds} rounds</span>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-ink-3">{stateSummary(state)}</p>
      {transitions.length ? (
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-2">
          {transitions.map((t) => (
            <li key={t.label}>
              <span className="text-ink-3">{t.label}</span> →{' '}
              {t.otherWorkflow ? `workflow ${t.target}` : humanize(t.target)}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  )
}

function WorkflowCard({
  projectId,
  workflow,
  isDefault,
  validation,
}: {
  projectId: string
  workflow: WorkflowDefinition
  isDefault: boolean
  validation?: { errors: string[]; warnings: string[] }
}) {
  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          {workflow.name}
          {isDefault ? (
            <span className="rounded-full bg-moss-soft px-2 py-0.5 text-xs font-semibold text-moss-strong">
              Default
            </span>
          ) : null}
        </span>
      }
      description={workflow.description ?? undefined}
      actions={
        <Link
          to="/projects/$projectId/workflows/$workflowKey/edit"
          params={{ projectId, workflowKey: workflow.key }}
          className="btn-secondary btn-sm"
        >
          Open editor
        </Link>
      }
    >
      <ol className="space-y-2.5">
        {workflow.states.map((state) => (
          <StateCard key={state.name} state={state} />
        ))}
      </ol>
      {validation && (validation.errors.length || validation.warnings.length) ? (
        <div className="mt-4 space-y-2">
          {validation.errors.length ? (
            <Notice tone="error">
              <ul className="space-y-0.5">
                {validation.errors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </Notice>
          ) : null}
          {validation.warnings.length ? (
            <Notice tone="signal">
              <ul className="space-y-0.5">
                {validation.warnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </Notice>
          ) : null}
        </div>
      ) : null}
    </Panel>
  )
}

/**
 * "New workflow" (ontwerp §3.1/§3.6): blank or from a template, both open
 * straight into the editor — nothing is written to the project until that
 * editor's own Save. Replaces the old immediate `fromTemplate` POST.
 */
function NewWorkflowDialog({
  open,
  onClose,
  projectId,
  workspaceId,
}: {
  open: boolean
  onClose: () => void
  projectId: string
  workspaceId: string
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { prompt, confirm, alert } = useDialogs()
  const templatesQuery = useQuery({
    queryKey: ['workflow-templates', workspaceId],
    queryFn: () => workflowsApi.templates(workspaceId),
    enabled: open && workspaceId !== '',
  })
  const templates = templatesQuery.data ?? []

  function openEditor(search: { template?: string; templateId?: string }): void {
    onClose()
    void navigate({
      to: '/projects/$projectId/workflows/$workflowKey/edit',
      params: { projectId, workflowKey: 'new' },
      search,
    })
  }

  // Manage workspace templates (ontwerp §6/§8, implementatieplan §1.4 fase
  // 3) right from this picker — built-in templates aren't editable here.
  function invalidateTemplates(): void {
    void queryClient.invalidateQueries({ queryKey: ['workflow-templates', workspaceId] })
  }
  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => workflowsApi.updateTemplate(id, { name }),
    onSuccess: invalidateTemplates,
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => workflowsApi.deleteTemplate(id),
    onSuccess: invalidateTemplates,
  })

  async function onRenameTemplate(id: string, currentName: string): Promise<void> {
    const name = await prompt({ title: 'Rename template', label: 'Name', defaultValue: currentName })
    if (!name || name === currentName) return
    try {
      await renameMutation.mutateAsync({ id, name })
    } catch (error) {
      await alert({
        kind: 'error',
        title: 'Couldn’t rename this template',
        message: error instanceof Error ? error.message : undefined,
      })
    }
  }

  async function onDeleteTemplate(id: string, name: string): Promise<void> {
    const ok = await confirm({
      kind: 'delete',
      title: `Delete "${name}"?`,
      message: 'Anyone starting a new workflow won’t see this template anymore. Workflows already built from it are unaffected.',
    })
    if (!ok) return
    try {
      await deleteMutation.mutateAsync(id)
    } catch (error) {
      await alert({
        kind: 'error',
        title: 'Couldn’t delete this template',
        message: error instanceof Error ? error.message : undefined,
      })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New workflow"
      description="Start blank, or from a template — you'll shape it on the canvas and nothing is saved until you choose to."
      size="lg"
    >
      {templatesQuery.isLoading ? (
        <LoadingRows label="Loading templates…" />
      ) : templatesQuery.isError ? (
        <ErrorNote error={templatesQuery.error} what="workflow templates" />
      ) : (
        <ul className="space-y-2">
          <li>
            <button
              type="button"
              onClick={() => openEditor({})}
              className="block w-full rounded-control border border-line-strong px-4 py-3 text-left transition-colors hover:bg-sunken"
            >
              <span className="flex items-center gap-2 font-medium text-ink">
                <ClayIcon name="workflow" size={20} />
                Blank
              </span>
              <span className="mt-1 block text-sm text-ink-2">
                Start with a single end state and build the flow yourself.
              </span>
            </button>
          </li>
          {templates.map((template) => (
            <li
              key={template.source === 'workspace' ? `w:${template.id}` : template.key}
              className="flex items-stretch gap-2"
            >
              <button
                type="button"
                onClick={() =>
                  openEditor(
                    template.source === 'workspace'
                      ? { templateId: template.id }
                      : { template: template.key }
                  )
                }
                className="block min-w-0 flex-1 rounded-control border border-line-strong px-4 py-3 text-left transition-colors hover:bg-sunken"
              >
                <span className="flex items-center gap-2 font-medium text-ink">
                  <ClayIcon name="workflow" size={20} />
                  {template.name}
                </span>
                <span className="mt-1 block text-sm text-ink-2">
                  {template.description}
                </span>
                {template.missingRoles?.length ? (
                  <span className="mt-1 block text-sm text-signal">
                    Needs roles this workspace doesn’t have yet:{' '}
                    {template.missingRoles.map(humanize).join(', ')}
                  </span>
                ) : null}
              </button>
              {template.source === 'workspace' && template.id ? (
                <div className="flex shrink-0 flex-col justify-center gap-1">
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() => void onRenameTemplate(template.id!, template.name)}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="btn-ghost btn-sm text-danger"
                    onClick={() => void onDeleteTemplate(template.id!, template.name)}
                  >
                    Delete
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  )
}

export function ProjectWorkflowsTab({ project }: { project: Project }) {
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const [creatingWorkflow, setCreatingWorkflow] = useState(false)

  const workflowsQuery = useQuery({
    queryKey: ['project', project.id, 'workflows'],
    queryFn: () => workflowsApi.get(project.id),
  })

  function invalidate(): void {
    void queryClient.invalidateQueries({
      queryKey: ['project', project.id, 'workflows'],
    })
    void queryClient.invalidateQueries({ queryKey: ['projects'] })
  }

  const setDefaultMutation = useMutation({
    mutationFn: (defaultWorkflow: string) =>
      workflowsApi.update(project.id, {
        defaultWorkflow,
        workflows: workflowsQuery.data!.workflows,
      }),
    onSuccess: invalidate,
  })

  if (workflowsQuery.isLoading) {
    return (
      <div className="panel">
        <LoadingRows label="Loading workflows…" />
      </div>
    )
  }
  if (workflowsQuery.isError) {
    return (
      <div className="panel">
        <ErrorNote error={workflowsQuery.error} what="workflows" />
      </div>
    )
  }

  const data = workflowsQuery.data!
  const workflows = data.workflows

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {workflows.length ? (
          <Field label="Default workflow" htmlFor="default-workflow">
            <select
              id="default-workflow"
              className="field-input h-9 w-auto py-0"
              value={data.defaultWorkflow ?? ''}
              onChange={(event) => setDefaultMutation.mutate(event.target.value)}
              disabled={setDefaultMutation.isPending}
            >
              {workflows.map((workflow) => (
                <option key={workflow.key} value={workflow.key}>
                  {workflow.name}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <p className="text-base text-ink-2">
            This project has no workflows yet. Tasks run as a single step
            until it does.
          </p>
        )}
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setCreatingWorkflow(true)}
        >
          New workflow
        </button>
      </div>

      {workflows.length === 0 ? (
        <div className="panel">
          <EmptyState
            title="No workflows yet"
            action={
              <button
                type="button"
                className="btn-primary"
                onClick={() => setCreatingWorkflow(true)}
              >
                New workflow
              </button>
            }
          >
            Start blank or from a built-in template for the task types you
            handle (feature, bug, hotfix, chore, spike).
          </EmptyState>
        </div>
      ) : (
        <div className="space-y-5">
          {workflows.map((workflow) => (
            <WorkflowCard
              key={workflow.key}
              projectId={project.id}
              workflow={workflow}
              isDefault={workflow.key === data.defaultWorkflow}
              validation={data.validation[workflow.key]}
            />
          ))}
        </div>
      )}

      <NewWorkflowDialog
        open={creatingWorkflow}
        onClose={() => setCreatingWorkflow(false)}
        projectId={project.id}
        workspaceId={workspaceId}
      />
    </div>
  )
}
